/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxLoadPanel from 'devextreme/ui/load_panel';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';


import { DxoAnimationModule } from './nested/animation';
import { DxoHideModule } from './nested/hide';
import { DxoShowModule } from './nested/show';
import { DxoPositionModule } from './nested/position';
import { DxoAtModule } from './nested/at';
import { DxoBoundaryOffsetModule } from './nested/boundary-offset';
import { DxoCollisionModule } from './nested/collision';
import { DxoMyModule } from './nested/my';
import { DxoOffsetModule } from './nested/offset';





/**
 * The LoadPanel is an overlay widget notifying the viewer that loading is in progress.
 */

export class DxLoadPanelComponent extends DxComponent implements OnDestroy {
    instance: DxLoadPanel;


    /**
     * Configures widget visibility animations. This object contains two fields: show and hide.
     */
    
    get animation(): any {
        return this._getOption('animation');
    }
    set animation(value: any) {
        this._setOption('animation', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is closed if a user clicks outside of the overlapping window.
     */
    
    get closeOnOutsideClick(): boolean|
        Function {
        return this._getOption('closeOnOutsideClick');
    }
    set closeOnOutsideClick(value: boolean|
        Function) {
        this._setOption('closeOnOutsideClick', value);
    }


    /**
     * Specifies whether widget content is rendered when the widget is shown or when rendering the widget.
     */
    
    get deferRendering(): boolean {
        return this._getOption('deferRendering');
    }
    set deferRendering(value: boolean) {
        this._setOption('deferRendering', value);
    }


    /**
     * The delay in milliseconds after which the load panel is displayed.
     */
    
    get delay(): number {
        return this._getOption('delay');
    }
    set delay(value: number) {
        this._setOption('delay', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether or not the widget can be focused.
     */
    
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * The height of the widget.
     */
    
    get height(): number {
        return this._getOption('height');
    }
    set height(value: number) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * A URL pointing to an image to be used as a load indicator.
     */
    
    get indicatorSrc(): string {
        return this._getOption('indicatorSrc');
    }
    set indicatorSrc(value: string) {
        this._setOption('indicatorSrc', value);
    }


    /**
     * Specifies the maximum height the widget can reach while resizing.
     */
    
    get maxHeight(): number|
        Function|
        string {
        return this._getOption('maxHeight');
    }
    set maxHeight(value: number|
        Function|
        string) {
        this._setOption('maxHeight', value);
    }


    /**
     * Specifies the maximum width the widget can reach while resizing.
     */
    
    get maxWidth(): number|
        Function|
        string {
        return this._getOption('maxWidth');
    }
    set maxWidth(value: number|
        Function|
        string) {
        this._setOption('maxWidth', value);
    }


    /**
     * The text displayed in the load panel.
     */
    
    get message(): string {
        return this._getOption('message');
    }
    set message(value: string) {
        this._setOption('message', value);
    }


    /**
     * Specifies the minimum height the widget can reach while resizing.
     */
    
    get minHeight(): number|
        Function|
        string {
        return this._getOption('minHeight');
    }
    set minHeight(value: number|
        Function|
        string) {
        this._setOption('minHeight', value);
    }


    /**
     * Specifies the minimum width the widget can reach while resizing.
     */
    
    get minWidth(): number|
        Function|
        string {
        return this._getOption('minWidth');
    }
    set minWidth(value: number|
        Function|
        string) {
        this._setOption('minWidth', value);
    }


    /**
     * An object defining widget positioning options.
     */
    
    get position(): DevExpress.positionConfig {
        return this._getOption('position');
    }
    set position(value: DevExpress.positionConfig) {
        this._setOption('position', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A Boolean value specifying whether or not the main screen is inactive while the widget is active.
     */
    
    get shading(): boolean {
        return this._getOption('shading');
    }
    set shading(value: boolean) {
        this._setOption('shading', value);
    }


    /**
     * Specifies the shading color.
     */
    
    get shadingColor(): string {
        return this._getOption('shadingColor');
    }
    set shadingColor(value: string) {
        this._setOption('shadingColor', value);
    }


    /**
     * A Boolean value specifying whether or not to show a load indicator.
     */
    
    get showIndicator(): boolean {
        return this._getOption('showIndicator');
    }
    set showIndicator(value: boolean) {
        this._setOption('showIndicator', value);
    }


    /**
     * A Boolean value specifying whether or not to show the pane behind the load indicator.
     */
    
    get showPane(): boolean {
        return this._getOption('showPane');
    }
    set showPane(value: boolean) {
        this._setOption('showPane', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number {
        return this._getOption('width');
    }
    set width(value: number) {
        this._setOption('width', value);
    }

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the hidden event.
     */
     onHidden: EventEmitter<any>;

    /**
     * A handler for the hiding event.
     */
     onHiding: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the showing event.
     */
     onShowing: EventEmitter<any>;

    /**
     * A handler for the shown event.
     */
     onShown: EventEmitter<any>;

    /**
     * A handler for the animationChange event.
     */
     animationChange: EventEmitter<any>;

    /**
     * A handler for the closeOnOutsideClickChange event.
     */
     closeOnOutsideClickChange: EventEmitter<boolean|
        Function>;

    /**
     * A handler for the deferRenderingChange event.
     */
     deferRenderingChange: EventEmitter<boolean>;

    /**
     * A handler for the delayChange event.
     */
     delayChange: EventEmitter<number>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
     focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the indicatorSrcChange event.
     */
     indicatorSrcChange: EventEmitter<string>;

    /**
     * A handler for the maxHeightChange event.
     */
     maxHeightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the maxWidthChange event.
     */
     maxWidthChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the messageChange event.
     */
     messageChange: EventEmitter<string>;

    /**
     * A handler for the minHeightChange event.
     */
     minHeightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the minWidthChange event.
     */
     minWidthChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the positionChange event.
     */
     positionChange: EventEmitter<DevExpress.positionConfig>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the shadingChange event.
     */
     shadingChange: EventEmitter<boolean>;

    /**
     * A handler for the shadingColorChange event.
     */
     shadingColorChange: EventEmitter<string>;

    /**
     * A handler for the showIndicatorChange event.
     */
     showIndicatorChange: EventEmitter<boolean>;

    /**
     * A handler for the showPaneChange event.
     */
     showPaneChange: EventEmitter<boolean>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number>;





    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            _watcherHelper: WatcherHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'hidden', emit: 'onHidden' },
            { subscribe: 'hiding', emit: 'onHiding' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'showing', emit: 'onShowing' },
            { subscribe: 'shown', emit: 'onShown' },
            { emit: 'animationChange' },
            { emit: 'closeOnOutsideClickChange' },
            { emit: 'deferRenderingChange' },
            { emit: 'delayChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'indicatorSrcChange' },
            { emit: 'maxHeightChange' },
            { emit: 'maxWidthChange' },
            { emit: 'messageChange' },
            { emit: 'minHeightChange' },
            { emit: 'minWidthChange' },
            { emit: 'positionChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'shadingChange' },
            { emit: 'shadingColorChange' },
            { emit: 'showIndicatorChange' },
            { emit: 'showPaneChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxLoadPanel(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }


static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-load-panel',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'animation': [{ type: Input },],
'closeOnOutsideClick': [{ type: Input },],
'deferRendering': [{ type: Input },],
'delay': [{ type: Input },],
'elementAttr': [{ type: Input },],
'focusStateEnabled': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'indicatorSrc': [{ type: Input },],
'maxHeight': [{ type: Input },],
'maxWidth': [{ type: Input },],
'message': [{ type: Input },],
'minHeight': [{ type: Input },],
'minWidth': [{ type: Input },],
'position': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'shading': [{ type: Input },],
'shadingColor': [{ type: Input },],
'showIndicator': [{ type: Input },],
'showPane': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onHidden': [{ type: Output },],
'onHiding': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onShowing': [{ type: Output },],
'onShown': [{ type: Output },],
'animationChange': [{ type: Output },],
'closeOnOutsideClickChange': [{ type: Output },],
'deferRenderingChange': [{ type: Output },],
'delayChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'focusStateEnabledChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'indicatorSrcChange': [{ type: Output },],
'maxHeightChange': [{ type: Output },],
'maxWidthChange': [{ type: Output },],
'messageChange': [{ type: Output },],
'minHeightChange': [{ type: Output },],
'minWidthChange': [{ type: Output },],
'positionChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'shadingChange': [{ type: Output },],
'shadingColorChange': [{ type: Output },],
'showIndicatorChange': [{ type: Output },],
'showPaneChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
};
}


export class DxLoadPanelModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxTemplateModule
  ],
  declarations: [
    DxLoadPanelComponent
  ],
  exports: [
    DxLoadPanelComponent,
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
